/**
 * Top-level renderer dispatcher for ADR-0079 typed-intent specs.
 *
 * `echartsSpecToOption(spec, rows, dimensions)` is the single public entry
 * point. It validates that every encoded column is present in `dimensions`,
 * then dispatches on `spec.chartType` to a per-type builder that returns
 * a fully-resolved `EChartsOption` (real `tooltip.formatter`, real
 * `axisLabel.formatter`, etc.).
 */

import type { EChartsOption } from "echarts";
import type { EchartsVizSpecV1 } from "../echartsSpec.js";
import { buildCartesianOption } from "./cartesian.js";
import { buildPieOption } from "./pie.js";
import { buildFunnelOption } from "./funnel.js";
import { buildGaugeOption } from "./gauge.js";
import { buildTreemapOption } from "./treemap.js";
import { buildCandlestickOption } from "./candlestick.js";
import { buildBoxplotOption } from "./boxplot.js";
import { buildRadarOption } from "./radar.js";
import { buildSankeyOption } from "./sankey.js";
import { buildHeatmapOption } from "./heatmap.js";
import { validateEncodedColumns } from "./shared.js";

export type EchartsRenderResult =
  | { ok: true; option: EChartsOption }
  | { ok: false; errors: string[] };

export function echartsSpecToOption(
  spec: EchartsVizSpecV1,
  rows: unknown[][],
  dimensions: string[],
): EchartsRenderResult {
  // Column-drift check (ADR-0079 §4.7). Run before dispatch so callers
  // surface a clean error rather than crashing inside a builder.
  const columnErrors = validateEncodedColumns(spec, dimensions);
  if (columnErrors.length > 0) {
    return { ok: false, errors: columnErrors };
  }

  let option: EChartsOption;
  switch (spec.chartType) {
    case "line":
    case "bar":
    case "area":
    case "scatter":
      option = buildCartesianOption(spec, rows, dimensions) as EChartsOption;
      break;
    case "pie":
    case "donut":
      option = buildPieOption(spec, rows, dimensions) as EChartsOption;
      break;
    case "funnel":
      option = buildFunnelOption(spec, rows, dimensions) as EChartsOption;
      break;
    case "gauge":
      option = buildGaugeOption(spec, rows, dimensions) as EChartsOption;
      break;
    case "treemap":
    case "sunburst":
      option = buildTreemapOption(spec, rows, dimensions) as EChartsOption;
      break;
    case "candlestick":
      option = buildCandlestickOption(spec, rows, dimensions) as EChartsOption;
      break;
    case "boxplot":
      option = buildBoxplotOption(spec, rows, dimensions) as EChartsOption;
      break;
    case "radar":
      option = buildRadarOption(spec, rows, dimensions) as EChartsOption;
      break;
    case "sankey":
      option = buildSankeyOption(spec, rows, dimensions) as EChartsOption;
      break;
    case "heatmap":
      option = buildHeatmapOption(spec, rows, dimensions) as EChartsOption;
      break;
  }

  // Clamp tooltip to the chart's own bounding rect so it can't overflow the
  // page frame when the chart sits near a viewport edge (e.g. the reactive-viz
  // inspector at the bottom of the editor). Only mutate tooltips the builder
  // already produced — never enable one that wasn't configured.
  if (option.tooltip !== undefined && option.tooltip !== null) {
    if (Array.isArray(option.tooltip)) {
      option.tooltip = option.tooltip.map((t) => ({ ...(t as object), confine: true })) as EChartsOption["tooltip"];
    } else {
      option.tooltip = { ...(option.tooltip as object), confine: true } as EChartsOption["tooltip"];
    }
  }

  return { ok: true, option };
}

export { validateEncodedColumns } from "./shared.js";
